import { useState, useEffect } from 'react';
import { client } from '../../api/client';

interface AiSetting {
  value: string | boolean;
  type: 'text' | 'textarea' | 'boolean' | 'json';
  description: string;
}

interface AiSettings {
  [key: string]: AiSetting;
}

const settingLabels: { [key: string]: string } = {
  ai_enabled: 'AI Asistent aktivan',
  ai_name: 'Ime AI asistenta',
  company_name: 'Naziv kompanije',
  company_description: 'Opis kompanije',
  contact_email: 'Kontakt email',
  contact_phone: 'Kontakt telefon',
  contact_info: 'Dodatne info o kontaktu',
  instagram_handle: 'Instagram handle',
  instagram_url: 'Instagram URL',
  ai_personality: 'Ličnost/uloga AI-a',
  ai_instructions: 'Dodatne instrukcije',
  welcome_message: 'Pozdravna poruka',
  fallback_message: 'Rezervna poruka (kad AI ne radi)',
  listing_analysis_enabled: 'Analiza oglasa aktivna',
  custom_knowledge: 'Dodatna znanja AI-a',
  topic_restrictions: 'Ograničenja tema',
  error_behavior: 'Poruka pri grešci/nedostupnosti',
  pricing_note: 'Napomene o cenama',
};

const settingOrder = [
  'ai_enabled',
  'ai_name',
  'company_name',
  'company_description',
  'contact_email',
  'contact_phone',
  'contact_info',
  'instagram_handle',
  'instagram_url',
  'ai_personality',
  'ai_instructions',
  'listing_analysis_enabled',
  'custom_knowledge',
  'topic_restrictions',
  'pricing_note',
  'welcome_message',
  'fallback_message',
  'error_behavior',
];

export function AiSettingsTab() {
  const [settings, setSettings] = useState<AiSettings>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await client.api.fetch('/admin/ai-settings');
      const data = await res.json();
      if (data.success) {
        setSettings(data.settings || {});
      } else {
        setError(data.error || 'Greška pri učitavanju podešavanja');
      }
    } catch (err) {
      setError('Greška pri povezivanju sa serverom. Pokrenite migraciju za kreiranje tabele.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key: string, value: string | boolean) => {
    setSettings(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        value,
      },
    }));
    setHasChanges(true);
    setSuccess(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // Convert settings to simple key-value pairs for saving
      const settingsToSave: { [key: string]: string | boolean } = {};
      Object.entries(settings).forEach(([key, setting]) => {
        settingsToSave[key] = setting.value;
      });

      const res = await client.api.fetch('/admin/ai-settings', {
        method: 'PUT',
        body: JSON.stringify({ settings: settingsToSave }),
      });
      const data = await res.json();

      if (data.success) {
        setSuccess('Podešavanja uspešno sačuvana!');
        setHasChanges(false);
      } else {
        setError(data.error || 'Greška pri čuvanju');
      }
    } catch (err) {
      setError('Greška pri povezivanju sa serverom');
    } finally {
      setSaving(false);
    }
  };

  const renderSettingInput = (key: string, setting: AiSetting) => {
    const label = settingLabels[key] || key;

    if (setting.type === 'boolean') {
      return (
        <div key={key} className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-700 rounded-lg">
          <div>
            <label className="font-medium text-slate-900 dark:text-white">{label}</label>
            <p className="text-sm text-slate-500 dark:text-slate-400">{setting.description}</p>
          </div>
          <button
            onClick={() => handleChange(key, !setting.value)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
              setting.value ? 'bg-amber-500' : 'bg-slate-300 dark:bg-slate-600'
            }`}
          >
            <span
              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                setting.value ? 'translate-x-6' : 'translate-x-1'
              }`}
            />
          </button>
        </div>
      );
    }

    if (setting.type === 'textarea') {
      return (
        <div key={key} className="space-y-2">
          <label className="block font-medium text-slate-900 dark:text-white">{label}</label>
          <p className="text-sm text-slate-500 dark:text-slate-400">{setting.description}</p>
          <textarea
            value={String(setting.value || '')}
            onChange={(e) => handleChange(key, e.target.value)}
            rows={4}
            className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent resize-none"
          />
        </div>
      );
    }

    return (
      <div key={key} className="space-y-2">
        <label className="block font-medium text-slate-900 dark:text-white">{label}</label>
        <p className="text-sm text-slate-500 dark:text-slate-400">{setting.description}</p>
        <input
          type="text"
          value={String(setting.value || '')}
          onChange={(e) => handleChange(key, e.target.value)}
          className="w-full px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-700 text-slate-900 dark:text-white focus:ring-2 focus:ring-amber-500 focus:border-transparent"
        />
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  const orderedSettings = settingOrder.filter(key => settings[key]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 dark:text-white">AI Asistent Podešavanja</h2>
          <p className="text-slate-600 dark:text-slate-400 mt-1">
            Konfiguriši kako AI asistent komunicira sa korisnicima
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving || !hasChanges}
          className={`px-6 py-2 rounded-lg font-semibold transition-colors ${
            hasChanges
              ? 'bg-amber-500 text-white hover:bg-amber-600'
              : 'bg-slate-300 text-slate-500 cursor-not-allowed'
          }`}
        >
          {saving ? 'Čuvanje...' : 'Sačuvaj promene'}
        </button>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="p-4 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-lg">
          <p className="font-medium">Greška</p>
          <p className="text-sm">{error}</p>
          {error.includes('migraciju') && (
            <p className="text-sm mt-2">
              Pokrenite SQL migraciju: <code className="bg-red-200 dark:bg-red-800 px-2 py-1 rounded">php-version/migrations/create_ai_settings_table.sql</code>
            </p>
          )}
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-lg">
          {success}
        </div>
      )}

      {/* Settings Sections */}
      {orderedSettings.length === 0 ? (
        <div className="bg-white dark:bg-slate-800 rounded-xl p-8 text-center">
          <svg className="w-16 h-16 mx-auto text-slate-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
          </svg>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-2">
            AI podešavanja nisu pronađena
          </h3>
          <p className="text-slate-600 dark:text-slate-400 mb-4">
            Potrebno je pokrenuti migraciju baze podataka
          </p>
          <code className="block bg-slate-100 dark:bg-slate-700 p-4 rounded-lg text-sm text-left overflow-x-auto">
            -- Pokrenite SQL fajl:<br />
            php-version/migrations/create_ai_settings_table.sql
          </code>
        </div>
      ) : (
        <>
          {/* Basic Settings */}
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 space-y-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white border-b border-slate-200 dark:border-slate-700 pb-2">
              🤖 Osnovna podešavanja
            </h3>
            {settings['ai_enabled'] && renderSettingInput('ai_enabled', settings['ai_enabled'])}
            {settings['ai_name'] && renderSettingInput('ai_name', settings['ai_name'])}
          </div>

          {/* Company Info */}
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 space-y-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white border-b border-slate-200 dark:border-slate-700 pb-2">
              🏢 Informacije o kompaniji
            </h3>
            {settings['company_name'] && renderSettingInput('company_name', settings['company_name'])}
            {settings['company_description'] && renderSettingInput('company_description', settings['company_description'])}
          </div>

          {/* Contact Info */}
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 space-y-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white border-b border-slate-200 dark:border-slate-700 pb-2">
              📞 Kontakt podaci
            </h3>
            {settings['contact_email'] && renderSettingInput('contact_email', settings['contact_email'])}
            {settings['contact_phone'] && renderSettingInput('contact_phone', settings['contact_phone'])}
            {settings['contact_info'] && renderSettingInput('contact_info', settings['contact_info'])}
            {settings['instagram_handle'] && renderSettingInput('instagram_handle', settings['instagram_handle'])}
            {settings['instagram_url'] && renderSettingInput('instagram_url', settings['instagram_url'])}
          </div>

          {/* AI Personality */}
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 space-y-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white border-b border-slate-200 dark:border-slate-700 pb-2">
              💬 Ličnost i ponašanje AI-a
            </h3>
            {settings['ai_personality'] && renderSettingInput('ai_personality', settings['ai_personality'])}
            {settings['ai_instructions'] && renderSettingInput('ai_instructions', settings['ai_instructions'])}
          </div>

          {/* AI Knowledge and Capabilities */}
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 space-y-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white border-b border-slate-200 dark:border-slate-700 pb-2">
              🧠 Znanja i mogućnosti AI-a
            </h3>
            {settings['listing_analysis_enabled'] && renderSettingInput('listing_analysis_enabled', settings['listing_analysis_enabled'])}
            {settings['custom_knowledge'] && renderSettingInput('custom_knowledge', settings['custom_knowledge'])}
            {settings['topic_restrictions'] && renderSettingInput('topic_restrictions', settings['topic_restrictions'])}
            {settings['pricing_note'] && renderSettingInput('pricing_note', settings['pricing_note'])}
            <div className="bg-amber-50 dark:bg-amber-900/20 rounded-lg p-4 text-sm text-amber-800 dark:text-amber-200">
              <p className="font-medium mb-1">💡 Napomena o cenama</p>
              <p>AI automatski učitava aktuelne cene iz admin panela (cenovnik). Ako imate posebne akcije ili napomene o cenama, unesite ih u polje iznad.</p>
            </div>
          </div>

          {/* Messages */}
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 space-y-4">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white border-b border-slate-200 dark:border-slate-700 pb-2">
              💌 Poruke
            </h3>
            {settings['welcome_message'] && renderSettingInput('welcome_message', settings['welcome_message'])}
            {settings['fallback_message'] && renderSettingInput('fallback_message', settings['fallback_message'])}
            {settings['error_behavior'] && renderSettingInput('error_behavior', settings['error_behavior'])}
          </div>
        </>
      )}

      {/* Help */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-6">
        <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-100 mb-2">
          ℹ️ Kako AI koristi ova podešavanja
        </h3>
        <ul className="text-sm text-blue-800 dark:text-blue-200 space-y-2">
          <li>• <strong>Opis kompanije</strong> - AI koristi ovo kada korisnici pitaju "Ko ste vi?" ili "O čemu je ovaj sajt?"</li>
          <li>• <strong>Kontakt podaci</strong> - AI daje ove informacije kada korisnik pita za kontakt</li>
          <li>• <strong>Ličnost AI-a</strong> - Definiše kako AI treba da se ponaša i odgovara</li>
          <li>• <strong>Dodatne instrukcije</strong> - Specifična pravila za AI (npr. "Uvek pitaj za lokaciju")</li>
          <li>• <strong>Analiza oglasa</strong> - Kada je aktivna, AI može analizirati nekretninu koju korisnik gleda (cena, lokacija, karakteristike)</li>
          <li>• <strong>Dodatna znanja</strong> - Informacije koje AI treba da zna (akcije, novosti, posebni uslovi)</li>
          <li>• <strong>Ograničenja tema</strong> - Dodatna pravila o čemu AI sme/ne sme da govori</li>
          <li>• <strong>Napomene o cenama</strong> - AI automatski čita cene iz cenovnika, ali ovde možete dodati posebne napomene</li>
          <li>• <strong>Poruka pri grešci</strong> - Prikazuje se kada AI nije dostupan ili dođe do greške</li>
          <li>• <strong>Rezervna poruka</strong> - Prikazuje se kada AI API nije dostupan</li>
        </ul>
      </div>
    </div>
  );
}
